import PropTypes from 'prop-types';

import Container, { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@classity-edtech/ui-base/dist/components/Inputs/Button/Button';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { CircularProgress } from '@classity-edtech/ui-base';

import { useGetPageContentQuery } from '../../services/seoContent';
import Helmet from '../../components/Helmet';
import Header from '../../components/SEO/Header';
import { Footer } from '../../components/SEO/Footer';
import { Link } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import { extractFooterContent, extractHeaderContent } from '../../utils/extractContents';
import GoogleButton from '../../components/GoogleButton';
import { RatingPromo } from '../../components/RatingPromo';

export interface HomeProps extends MuiContainerProps {}

export const Home = ({ className }: HomeProps) => {
  const screens = useAppMediaQuery();
  const smallScreens = screens.isMobile || screens.isTablet;

  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'coming_soon',
    contentType: 'page',
  });

  const extractContent = () => {
    if (!!data) {
      return {
        seo: {
          title: data?.seo?.fields?.titleTag,
          keywords: data?.seo?.fields?.keywords,
          description: data?.seo?.fields?.metaDescription,
          metaRobotsIndex: data?.seo?.fields?.metaRobotsIndex,
        },
      };
    }
  };

  const extractHeroContent = () => {
    const content = data?.sections?.[0];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      primaryCta: content?.fields?.list?.[0]?.fields?.reference?.fields?.label ?? '',
      heroImage: content?.fields?.list?.[2]?.fields?.media?.fields?.image?.fields?.file?.url ?? '',
    };
  };

  return (
    <>
      <Helmet
        title={extractContent()?.seo?.title}
        keywords={extractContent()?.seo?.keywords}
        description={extractContent()?.seo?.description}
        metaRobotsIndex={extractContent()?.seo?.metaRobotsIndex}
      />

      <GoogleButton useOneTap />

      {fetchingContent ? (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Header {...extractHeaderContent(data)} hideNavigation={true} brand={'classity'} />

          <Box
            sx={{
              background: globalColours['$global-colour-background-screen-default'],
              mt: '0',
            }}
          >
            <Container
              sx={{
                minHeight: { xs: '100%', md: '100dvh' },
                display: 'flex',
                alignItems: 'center',
                px: { xs: 2.5, md: 4 },
                py: { xs: 5, md: 0 },
                gap: { xs: 5, md: 0 },
                justifyContent: 'center',
                flexDirection: { xs: 'column-reverse', md: 'column' },
              }}
            >
              <Stack sx={{ gap: 1.5, maxWidth: { xs: '100%', md: '640px' }, pb: { xs: 5 } }}>
                <Typography
                  variant={smallScreens ? 'subtitle2' : 'subtitle1'}
                  variantMapping={{ subtitle1: 'h1', subtitle2: 'h1' }}
                  component='h1'
                  sx={{ textAlign: 'center' }}
                >
                  {extractHeroContent()?.title}
                </Typography>
                <Typography
                  variant={smallScreens ? 'body2' : 'body1'}
                  sx={{ textAlign: 'center' }}
                  variantMapping={{ body1: 'h2', body2: 'h2' }}
                >
                  {extractHeroContent()?.description}
                </Typography>
                <Link to={`/${webPaths.signup}`} style={{ textDecoration: 'none', width: '100%' }}>
                  <Button sx={{ width: '100%', px: 3, py: 1.5, mt: { xs: 0, md: 0.5 } }}>
                    <Typography variant='body2SemiBold'>
                      {extractHeroContent()?.primaryCta}
                    </Typography>
                  </Button>
                </Link>
              </Stack>

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={'https://static04.classity.co/web/public/Classroom.svg'}
                  alt='Small group class lesson'
                  style={{
                    width: '100%',
                    display: 'block',
                    maxWidth: smallScreens ? '400px' : '600px',
                  }}
                />
              </Box>
            </Container>

            <Container
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: { xs: 2.5, md: 4 },
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  pb: smallScreens ? 4 : 6,
                }}
              >
                <RatingPromo
                  useSprite={true}
                  spriteUrl='https://static04.classity.co/web/public/avatars-sprite.png'
                  spriteSize={124}
                  spriteColumns={7}
                  showOffer={false}
                />
              </Box>
            </Container>
          </Box>

          <Footer {...extractFooterContent(data)} />
        </>
      )}
    </>
  );
};

Home.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Home.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Home)(
  ({ theme }) => `
`,
);
